import React, { useState, useMemo } from 'react';
import EventListItem from '../list/EventListItem';
import ExpandButton from '../shared/ExpandButton';
import GroupMatchEvent from './GroupMatchEvent';
import MatchEvent from './MatchEvent';
import NoteEvent from './NoteEvent';

/**
 * AssignmentEvent - Renders an assignment and its children
 * Handles both open and closed assignments
 */
function AssignmentEvent({
  assignment,
  allEvents,
  expandedGroupMatches,
  onToggleGroupMatch,
  onToggleAssignmentStatus,
  onEventClick,
  onDeleteClick,
  disabled
}) {
  const isOpen = assignment.status === 'open';
  const isClosed = assignment.status === 'closed';

  // Local expand state for closed assignments (open assignments are always expanded)
  const [isExpanded, setIsExpanded] = useState(isOpen);

  // Get all children of this assignment, sorted by date (newest first)
  const children = useMemo(() => {
    return allEvents
      .filter(e => e.metadata?.assignmentEventId === assignment.eventId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [allEvents, assignment.eventId]);

  const handleToggleExpand = () => {
    if (!isOpen) { // Only allow toggling for closed assignments
      setIsExpanded(!isExpanded);
    }
  };

  // Open assignments: no expand button (always expanded)
  // Closed assignments: show expand button
  const expandButton = isOpen ? null : (
    <ExpandButton
      eventId={assignment.eventId}
      isExpanded={isExpanded}
      onToggle={handleToggleExpand}
    />
  );

  const shouldShowChildren = isOpen || isExpanded;

  return (
    <>
      {/* Assignment header */}
      <EventListItem
        event={assignment}
        onClick={onEventClick}
        onDelete={onDeleteClick}
        onToggleAssignment={onToggleAssignmentStatus}
        disabled={disabled}
        expandButton={expandButton}
      />

      {/* Children (only if expanded or open) - rendered in chronological order */}
      {shouldShowChildren && (
        <>
          {children.map(child => {
            if (child.eventType === 'group_match') {
              return (
                <GroupMatchEvent
                  key={child.eventId}
                  groupMatch={child}
                  allEvents={allEvents}
                  expandedGroupMatches={expandedGroupMatches}
                  onToggleGroupMatch={onToggleGroupMatch}
                  onEventClick={onEventClick}
                  onDeleteClick={onDeleteClick}
                  disabled={disabled}
                />
              );
            }

            if (child.eventType === 'match' && !child.metadata?.groupEventId) {
              return (
                <MatchEvent
                  key={child.eventId}
                  match={child}
                  onEventClick={onEventClick}
                  onDeleteClick={onDeleteClick}
                  disabled={disabled}
                />
              );
            }

            if (child.eventType === 'note') {
              return (
                <NoteEvent
                  key={child.eventId}
                  note={child}
                  onEventClick={onEventClick}
                  onDeleteClick={onDeleteClick}
                  disabled={disabled}
                />
              );
            }

            return null;
          })}
        </>
      )}
    </>
  );
}

export default React.memo(AssignmentEvent);
